import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  CheckSquare, Square, Users, FileText, AlertTriangle, CheckCircle,
  Clock, User, Shield, X, RefreshCw, Search, ChevronDown, ChevronRight,
  UserCheck, Download,
} from 'lucide-react';
import { complianceExecutionApi, CompanyCompliancePage } from '@domains/compliance/execution/api/complianceExecution.api';
import { usersApi } from '@domains/admin/api/users.api';
import { companiesApi, CompanyFramework } from '@domains/companies/api';
import { auditPeriodApi } from '@domains/compliance/lifecycle/VerificationPanel';
import { useModal, ConfirmModal } from '@components/modal';
import { ShieldCheck, Lock as LockIcon } from 'lucide-react';
import { notify } from '../../../core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';

interface ComplianceUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company_id: number | null;
  is_active: boolean;
}

type StatusFilter = 'all' | 'unassigned' | 'not_started' | 'in_progress' | 'submitted' | 'approved_for_audit';

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  approved_for_audit: 'Approved',
  certified: 'Certified',
};

const STATUS_STYLE: Record<string, string> = {
  not_started: 'bg-slate-50 text-slate-600 border border-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 border border-blue-200',
  submitted: 'bg-amber-50 text-amber-700 border border-amber-200',
  approved_for_audit: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  certified: 'bg-violet-50 text-violet-700 border border-violet-200',
};

const PRIORITY_STYLE: Record<string, string> = {
  critical: 'bg-red-50 text-red-700 border border-red-200',
  high: 'bg-orange-50 text-orange-700 border border-orange-200',
  medium: 'bg-blue-50 text-blue-700 border border-blue-200',
  low: 'bg-slate-50 text-slate-600 border border-slate-200',
};

const StatusIcon: React.FC<{ status: string; size?: number }> = ({ status, size = 14 }) => {
  if (status === 'approved_for_audit') return <CheckCircle size={size} />;
  if (status === 'submitted') return <FileText size={size} />;
  if (status === 'in_progress') return <RefreshCw size={size} />;
  return <Clock size={size} />;
};

const CompanyCompliancePages: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [pages, setPages] = useState<CompanyCompliancePage[]>([]);
  const [complianceUsers, setComplianceUsers] = useState<ComplianceUser[]>([]);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Assign / reassign modal
  const [assignModal, setAssignModal] = useState<{ mode: 'assign' | 'reassign'; pageIds: number[] } | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [assignDueDate, setAssignDueDate] = useState<string>('');

  // Expanded framework groups
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Per-group visible page count (load more)
  const PAGE_SIZE = 10;
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});

  const canManage = hasPermission('company.compliance.manage');
  const { openModal } = useModal();
  const [frameworksMeta, setFrameworksMeta] = useState<CompanyFramework[]>([]);
  const [openAuditPeriods, setOpenAuditPeriods] = useState<Record<number, any>>({});
  const [requestingAuditFor, setRequestingAuditFor] = useState<number | null>(null);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!assignModal) { setAssignDueDate(''); return; }
    if (assignModal.pageIds.length === 1) {
      const p = pages.find(pp => pp.id === assignModal.pageIds[0]);
      setAssignDueDate(p?.due_date ? String(p.due_date).slice(0, 10) : '');
    } else {
      setAssignDueDate('');
    }
  }, [assignModal, pages]);

  const handleRequestAudit = (fw: CompanyFramework) => {
    if (!fw.assignment_id) return;
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Request External Audit',
        message:
          `Open an external audit period for "${fw.name}"? This will LOCK all ${fw.total_pages} compliance pages immediately — no further edits while the auditor reviews.\n\n` +
          `What happens next:\n` +
          `• If a certificate is issued, the pages stay LOCKED permanently as the immutable certified record (this is what the certificate attests to).\n` +
          `• If the audit is cancelled before certification, the pages unlock and become editable again.\n\n` +
          `Only proceed if you and the framework owner agree the evidence is final.`,
        confirmText: 'Open Audit Period',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          setRequestingAuditFor(fw.assignment_id);
          try {
            const result = await auditPeriodApi.open(fw.assignment_id);
            if (!result?.success) {
              notify.error(result?.message ?? 'Failed to open audit period');
              return;
            }
            notify.success(`Audit period opened. ${result.data?.pagesLocked ?? 0} pages locked.`);
            await loadData();
          } catch (e: any) {
            notify.error(e?.message ?? 'Failed to open audit period');
          } finally {
            setRequestingAuditFor(null);
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleExportCSV = () => {
    const csv = [
      ['Framework', 'Section', 'Title', 'Status', 'Priority', 'Assigned To', 'Due Date', 'Submitted', 'Evidence Required'].join(','),
      ...pages.map(p => [
        `"${(p.framework_name || '').replace(/"/g, '""')}"`,
        `"${(p.section || '').replace(/"/g, '""')}"`,
        `"${(p.title || '').replace(/"/g, '""')}"`,
        p.status,
        p.priority || 'medium',
        `"${(p.assigned_to_name || 'Unassigned').replace(/"/g, '""')}"`,
        p.due_date ? new Date(p.due_date).toLocaleDateString() : '',
        p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : '',
        p.evidence_required ? 'Yes' : 'No',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-pages-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const pagesResult = await complianceExecutionApi.getCompanyCompliancePages();
      setPages(pagesResult);

      // Load assignable compliance users (backend filters out admins/auditors)
      try {
        const usersRes = await fetch('/api/compliance/execution/company/assignable-users', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
        if (usersRes.ok) {
          const usersJson = await usersRes.json();
          setComplianceUsers(usersJson.data || []);
        }
      } catch {
        // Fallback to all company users
        const usersResult = await usersApi.getUsersByCompany(user?.company_id ?? 1);
        setComplianceUsers(usersResult);
      }
      // Auto-expand all framework groups initially
      const groups = new Set(pagesResult.map(p => p.framework_name || 'Unknown Framework'));
      setExpanded(groups);

      // Load framework assignments + open audit periods for the per-framework
      // "Request External Audit" CTA in each group header.
      try {
        const fw = await companiesApi.getCompanyFrameworks();
        setFrameworksMeta(fw);
      } catch { /* non-blocking */ }
      try {
        const periods = await auditPeriodApi.listForCompany();
        const map: Record<number, any> = {};
        for (const p of (periods as any[])) {
          if (p.status === 'open' || p.status === 'locked') {
            map[p.framework_assignment_id] = p;
          }
        }
        setOpenAuditPeriods(map);
      } catch { /* non-blocking */ }
    } catch {
      notify.error('Failed to load compliance pages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUser || !assignModal || assignModal.pageIds.length === 0) return;
    setIsAssigning(true);
    try {
      await complianceExecutionApi.assignCompliancePages(assignModal.pageIds, selectedUser, assignDueDate || null);
      const verb = assignModal.mode === 'reassign' ? 'reassigned' : 'assigned';
      const n = assignModal.pageIds.length;
      notify.success(`${n} page${n !== 1 ? 's' : ''} ${verb} successfully`);
      setSelectedPages(new Set());
      setSelectedUser(null);
      setAssignModal(null);
      loadData();
    } catch (e: any) {
      notify.error(e?.response?.data?.message || e?.message || `Failed to ${assignModal.mode === 'reassign' ? 'reassign' : 'assign'} pages`);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async (pageIds: number[]) => {
    try {
      await complianceExecutionApi.unassignCompliancePages(pageIds);
      notify.success(`${pageIds.length} page${pageIds.length !== 1 ? 's' : ''} unassigned`);
      setSelectedPages(new Set());
      loadData();
    } catch {
      notify.error('Failed to unassign pages');
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────────

  const frameworks = useMemo(() =>
    Array.from(new Set(pages.map(p => p.framework_name))).sort(),
    [pages]);

  const filtered = useMemo(() => {
    return pages.filter(p => {
      if (search && !p.title.toLowerCase().includes(search.toLowerCase()) &&
          !p.section.toLowerCase().includes(search.toLowerCase())) return false;
      if (frameworkFilter && p.framework_name !== frameworkFilter) return false;
      if (priorityFilter && p.priority !== priorityFilter) return false;
      if (statusFilter === 'unassigned') return p.assigned_to === null;
      if (statusFilter !== 'all') return p.status === statusFilter;
      return true;
    });
  }, [pages, search, frameworkFilter, priorityFilter, statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, CompanyCompliancePage[]>();
    filtered.forEach(p => {
      const key = p.framework_name || 'Unknown Framework';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    return map;
  }, [filtered]);

  const unassignedCount = pages.filter(p => p.assigned_to === null).length;
  const assignedCount = pages.length - unassignedCount;
  const selectedUnassigned = Array.from(selectedPages).filter(id => {
    const p = pages.find(p => p.id === id);
    return p && p.assigned_to === null;
  });

  const togglePage = (id: number) => {
    const next = new Set(selectedPages);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedPages(next);
  };

  const toggleGroup = (fw: string) => {
    const next = new Set(expanded);
    next.has(fw) ? next.delete(fw) : next.add(fw);
    setExpanded(next);
  };

  const selectAllVisible = () => {
    const unassigned = filtered.filter(p => p.assigned_to === null).map(p => p.id);
    const allSelected = unassigned.every(id => selectedPages.has(id));
    if (allSelected) {
      const next = new Set(selectedPages);
      unassigned.forEach(id => next.delete(id));
      setSelectedPages(next);
    } else {
      setSelectedPages(prev => new Set([...prev, ...unassigned]));
    }
  };

  if (!canManage) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <Shield className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900">Access Denied</h2>
          <p className="text-slate-500 text-sm mt-1">Only Company Admins can manage compliance page assignments.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Compliance Pages</h1><PageInfoButton /></div>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-full">
                {pages.length} pages
              </span>
              {unassignedCount > 0 && (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-full border border-amber-200">
                  <AlertTriangle className="w-3 h-3" />
                  {unassignedCount} unassigned
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm">Assign and track framework requirements across your team</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              disabled={pages.length === 0}
              className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={loadData}
              className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Pages', value: pages.length, sub: `${grouped.size} framework${grouped.size !== 1 ? 's' : ''}`, icon: <FileText size={18} className="text-blue-600" /> },
            { label: 'Assigned', value: assignedCount, sub: pages.length > 0 ? `${Math.round((assignedCount / pages.length) * 100)}% coverage` : '--', icon: <UserCheck size={18} className="text-emerald-600" /> },
            { label: 'Unassigned', value: unassignedCount, sub: unassignedCount > 0 ? 'Needs attention' : 'All pages assigned', icon: <AlertTriangle size={18} className="text-amber-600" /> },
            { label: 'Team Members', value: complianceUsers.length, sub: 'Available to assign', icon: <Users size={18} className="text-slate-500" /> },
          ].map(card => (
            <div key={card.label} className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-1">
                {card.icon}
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{card.label}</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{card.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by title or section..."
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white placeholder:text-slate-400"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-medium">
                  Clear
                </button>
              )}
            </div>

            {/* Status pills */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 flex-shrink-0 flex-wrap">
              {([
                { key: 'all', label: `All (${pages.length})` },
                { key: 'unassigned', label: `Unassigned (${unassignedCount})` },
                { key: 'in_progress', label: 'In Progress' },
                { key: 'submitted', label: 'Submitted' },
                { key: 'approved_for_audit', label: 'Approved' },
              ] as { key: StatusFilter; label: string }[]).map(t => (
                <button
                  key={t.key}
                  onClick={() => setStatusFilter(t.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    statusFilter === t.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <select
              value={frameworkFilter}
              onChange={e => setFrameworkFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-700 flex-shrink-0"
            >
              <option value="">All Frameworks</option>
              {frameworks.map(fw => (
                <option key={fw} value={fw}>{fw}</option>
              ))}
            </select>

            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-700 flex-shrink-0"
            >
              <option value="">All Priorities</option>
              {['critical', 'high', 'medium', 'low'].map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedPages.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-800">
                {selectedPages.size} page{selectedPages.size !== 1 ? 's' : ''} selected
              </span>
              <button onClick={() => setSelectedPages(new Set())} className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline">
                Clear
              </button>
            </div>
            {selectedUnassigned.length > 0 && (
              <button
                onClick={() => { setAssignModal({ mode: 'assign', pageIds: selectedUnassigned }); setSelectedUser(null); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Users className="w-4 h-4" /> Assign {selectedUnassigned.length} page{selectedUnassigned.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}

        {/* Grouped pages */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
            <FileText className="w-10 h-10 text-slate-300 mb-3" />
            <h3 className="font-semibold text-slate-700">No pages match your filters</h3>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {Array.from(grouped.entries()).map(([fw, fwPages]) => {
              const isOpen = expanded.has(fw);
              const visible = visibleCounts[fw] ?? PAGE_SIZE;
              const visiblePages = fwPages.slice(0, visible);
              const remaining = fwPages.length - visible;
              const fwAssigned = fwPages.filter(p => p.assigned_to !== null).length;
              const fwPct = fwPages.length > 0 ? Math.round((fwAssigned / fwPages.length) * 100) : 0;
              const allUnassignedSelected = fwPages.filter(p => !p.assigned_to).every(p => selectedPages.has(p.id));
              const hasUnassigned = fwPages.some(p => !p.assigned_to);

              // Per-framework audit-readiness state
              const fwApproved = fwPages.filter(p => p.status === 'approved_for_audit' || p.status === 'certified').length;
              const allApproved = fwPages.length > 0 && fwApproved === fwPages.length;
              const fwMeta = frameworksMeta.find(f => f.name === fw);
              const openAudit = fwMeta?.assignment_id ? openAuditPeriods[fwMeta.assignment_id] : null;

              return (
                <div key={fw} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {/* Framework header */}
                  <div
                    className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50 transition-colors select-none border-b border-slate-100"
                    onClick={() => toggleGroup(fw)}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                    <div className="font-semibold text-slate-800 flex-1">{fw}</div>
                    <div className="hidden sm:flex items-center gap-3">
                      <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${fwPct}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{fwAssigned}/{fwPages.length} assigned</span>
                    </div>

                    {/* Per-framework audit / cert state */}
                    {canManage && fwMeta && (() => {
                      // Certificate already issued — show cert pill, no further actions
                      if (fwMeta.certification_id) {
                        return (
                          <span
                            className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1"
                            onClick={e => e.stopPropagation()}
                            title={`Certificate ${fwMeta.certificate_number ?? ''} issued${fwMeta.certification_expires_at ? ` · expires ${new Date(fwMeta.certification_expires_at).toLocaleDateString()}` : ''}`}
                          >
                            <ShieldCheck className="w-3 h-3" /> Certificate issued
                          </span>
                        );
                      }
                      if (openAudit) {
                        return (
                          <span
                            className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1"
                            title={`External audit period ${openAudit.status === 'locked' ? 'locked' : 'open'} — pages cannot be edited until it closes.`}
                            onClick={e => e.stopPropagation()}
                          >
                            <LockIcon className="w-3 h-3" /> Audit in progress
                          </span>
                        );
                      }
                      if (allApproved) {
                        return (
                          <button
                            onClick={e => { e.stopPropagation(); handleRequestAudit(fwMeta); }}
                            disabled={requestingAuditFor === fwMeta.assignment_id}
                            className="text-xs font-medium px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                            title="All pages approved — open an external audit period"
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {requestingAuditFor === fwMeta.assignment_id ? 'Opening…' : 'Request External Audit'}
                          </button>
                        );
                      }
                      if (fwApproved > 0) {
                        return (
                          <span
                            className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg font-medium"
                            title={`${fwPages.length - fwApproved} more page${fwPages.length - fwApproved !== 1 ? 's' : ''} need approval before requesting external audit`}
                            onClick={e => e.stopPropagation()}
                          >
                            {fwApproved}/{fwPages.length} approved
                          </span>
                        );
                      }
                      return null;
                    })()}

                    {hasUnassigned && canManage && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const ids = fwPages.filter(p => !p.assigned_to).map(p => p.id);
                          setAssignModal({ mode: 'assign', pageIds: ids });
                          setSelectedUser(null);
                        }}
                        className="text-xs text-blue-600 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-50 font-medium"
                      >
                        Assign all
                      </button>
                    )}
                    {canManage && hasUnassigned && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const ids = fwPages.filter(p => !p.assigned_to).map(p => p.id);
                          if (allUnassignedSelected) {
                            const next = new Set(selectedPages);
                            ids.forEach(id => next.delete(id));
                            setSelectedPages(next);
                          } else {
                            setSelectedPages(new Set([...selectedPages, ...ids]));
                          }
                        }}
                        className="ml-1"
                      >
                        {allUnassignedSelected
                          ? <CheckSquare className="w-4 h-4 text-blue-600" />
                          : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    )}
                  </div>

                  {/* Pages list */}
                  {isOpen && (
                    <div className="divide-y divide-slate-100">
                      {visiblePages.map(page => {
                        const isSelected = selectedPages.has(page.id);
                        const isUnassigned = page.assigned_to === null;
                        return (
                          <div
                            key={page.id}
                            className={`flex items-start gap-3 px-5 py-3 transition-colors ${
                              isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'
                            }`}
                          >
                            {/* Checkbox */}
                            <div className="pt-0.5 w-5 flex-shrink-0">
                              {isUnassigned && canManage ? (
                                <button onClick={() => togglePage(page.id)}>
                                  {isSelected
                                    ? <CheckSquare className="w-4 h-4 text-blue-600" />
                                    : <Square className="w-4 h-4 text-slate-300 hover:text-slate-400" />}
                                </button>
                              ) : (
                                <div className="w-4 h-4" />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span className="font-medium text-slate-900 text-sm">{page.title}</span>
                                <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full font-medium">{page.section}</span>
                              </div>
                              {page.description && (
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{page.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[page.status] || 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
                                  <StatusIcon status={page.status} size={10} />
                                  {STATUS_LABEL[page.status] || page.status}
                                </span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${PRIORITY_STYLE[page.priority] || 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
                                  {page.priority}
                                </span>
                                {page.assigned_to_name ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                                    <User className="w-3 h-3" /> {page.assigned_to_name}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                    <AlertTriangle className="w-3 h-3" /> Unassigned
                                  </span>
                                )}
                                {page.due_date && (
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${new Date(page.due_date) < new Date() ? 'text-red-600 bg-red-50 border-red-200' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                                    <Clock className="w-3 h-3" />
                                    {new Date(page.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                                {/* Evidence indicator */}
                                {(page.evidence_count > 0 || page.evidence_required) && (
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                                    page.evidence_count === 0 ? 'text-slate-400 bg-slate-50 border-slate-200' :
                                    page.evidence_rejected > 0 ? 'text-red-600 bg-red-50 border-red-200' :
                                    page.evidence_verified > 0 && page.evidence_verified === page.evidence_count ? 'text-emerald-600 bg-emerald-50 border-emerald-200' :
                                    'text-amber-600 bg-amber-50 border-amber-200'
                                  }`}>
                                    <FileText className="w-3 h-3" />
                                    {page.evidence_count > 0 ? `${page.evidence_verified || 0}/${page.evidence_count} verified` : 'No evidence'}
                                  </span>
                                )}
                                {/* Auditor feedback */}
                                {page.audit_findings && (
                                  <span className="inline-flex items-center gap-1 text-xs text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                                    Feedback
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            {canManage && (
                              <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                {isUnassigned ? (
                                  <button
                                    onClick={() => { setAssignModal({ mode: 'assign', pageIds: [page.id] }); setSelectedUser(null); }}
                                    className="text-xs text-blue-600 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-50 font-medium transition-colors"
                                  >
                                    Assign
                                  </button>
                                ) : page.status === 'approved_for_audit' || page.status === 'certified' ? (
                                  <span
                                    className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
                                    title="Page is approved and locked for audit. Request Changes from the auditor view to re-open it."
                                  >
                                    <CheckCircle className="w-3 h-3" /> Locked
                                  </span>
                                ) : page.status === 'submitted' ? (
                                  <span
                                    className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
                                    title="Page is awaiting internal auditor review. Reassigning now would lose the submission."
                                  >
                                    <FileText className="w-3 h-3" /> In Review
                                  </span>
                                ) : (
                                  <div className="flex gap-1.5">
                                    <button
                                      onClick={() => { setAssignModal({ mode: 'reassign', pageIds: [page.id] }); setSelectedUser(null); }}
                                      className="text-xs text-slate-600 border border-slate-200 px-2.5 py-1 rounded-lg hover:bg-slate-50 font-medium flex items-center gap-1 transition-colors"
                                    >
                                      <UserCheck className="w-3 h-3" /> Reassign
                                    </button>
                                    <button
                                      onClick={() => handleUnassign([page.id])}
                                      className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-50 font-medium flex items-center gap-1 transition-colors"
                                    >
                                      <X className="w-3 h-3" /> Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Load more / collapse */}
                      {remaining > 0 && (
                        <div className="px-5 py-3 flex items-center justify-between bg-slate-50 border-t border-slate-100">
                          <span className="text-xs text-slate-400">{remaining} more page{remaining !== 1 ? 's' : ''} not shown</span>
                          <div className="flex gap-3">
                            <button
                              onClick={() => setVisibleCounts(prev => ({ ...prev, [fw]: visible + PAGE_SIZE }))}
                              className="text-xs font-medium text-blue-600 hover:underline"
                            >
                              Load {Math.min(remaining, PAGE_SIZE)} more
                            </button>
                            <button
                              onClick={() => setVisibleCounts(prev => ({ ...prev, [fw]: fwPages.length }))}
                              className="text-xs font-medium text-slate-500 hover:underline"
                            >
                              Show all {fwPages.length}
                            </button>
                          </div>
                        </div>
                      )}
                      {visible > PAGE_SIZE && remaining === 0 && (
                        <div className="px-5 py-3 text-right bg-slate-50 border-t border-slate-100">
                          <button
                            onClick={() => setVisibleCounts(prev => ({ ...prev, [fw]: PAGE_SIZE }))}
                            className="text-xs text-slate-400 hover:text-slate-600 hover:underline"
                          >
                            Collapse
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Select all bar */}
        {filtered.some(p => !p.assigned_to) && canManage && selectedPages.size === 0 && (
          <div className="text-center mt-4">
            <button onClick={selectAllVisible} className="text-xs text-blue-600 font-medium hover:underline">
              Select all unassigned pages visible ({filtered.filter(p => !p.assigned_to).length})
            </button>
          </div>
        )}

        {/* Footer */}
        {filtered.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl px-5 py-3 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of <span className="font-semibold text-slate-700">{pages.length}</span> pages
            </span>
            <span className="text-xs text-slate-500">
              <span className="font-semibold text-emerald-600">{assignedCount}</span> assigned · <span className="font-semibold text-amber-600">{unassignedCount}</span> unassigned · <span className="font-semibold text-blue-600">{complianceUsers.length}</span> team members
            </span>
          </div>
        )}
      </div>

      {/* Assign / Reassign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4" />
                {assignModal.mode === 'reassign' ? 'Reassign Page' : `Assign ${assignModal.pageIds.length} Page${assignModal.pageIds.length !== 1 ? 's' : ''}`}
              </h2>
              <button onClick={() => setAssignModal(null)} className="text-white/60 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {assignModal.pageIds.length <= 5 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5">
                  {assignModal.pageIds.map(id => {
                    const p = pages.find(p => p.id === id);
                    return p ? (
                      <div key={id} className="text-xs text-slate-700 flex items-center gap-2">
                        <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate font-medium">{p.title}</span>
                        <span className="text-slate-400 flex-shrink-0">{p.framework_name}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
              {assignModal.pageIds.length > 5 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 font-medium">
                  {assignModal.pageIds.length} pages will be assigned
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {assignModal.mode === 'reassign' ? 'Reassign to' : 'Assign to'}
                </label>
                <select
                  value={selectedUser || ''}
                  onChange={e => setSelectedUser(parseInt(e.target.value))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose a team member...</option>
                  {complianceUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Due date <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={assignDueDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setAssignDueDate(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {assignDueDate
                    ? `Owner will be reminded and the page will be flagged overdue after ${new Date(assignDueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
                    : 'Leave blank if there is no deadline. You can set it later.'}
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => setAssignModal(null)}
                className="px-4 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={isAssigning || !selectedUser}
                className="px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2 transition-colors"
              >
                {isAssigning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                {assignModal.mode === 'reassign' ? 'Reassign' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCompliancePages;
